import React, { createContext, useContext, useState, useRef, useCallback, useEffect, useMemo, memo } from "react";
import PropTypes from "prop-types";
import { io } from "socket.io-client";
import configs from "../../utils/configs";
import { getToken } from "../../utils/getToken";
import aiAgents from "../../singleton/AIAgents";
import { MediaDevicesEvents } from "../../utils/media-devices-utils";
import { proxiedUrlFor } from "../../utils/media-url-utils";
import {
  downsampleFloat32,
  float32ToInt16,
  splitInt16IntoChunks,
  buildStartTranscriptionPayload,
  buildReceiveSubscriptionPayload,
  shouldConnectSendTranscriptionSession,
  buildCaptionAdmissionKey,
  applyTranscriptDelta,
  applyTranscriptDone,
  appendTranslation,
  pruneExpiredCaptions,
  pruneDynamicallyInaudibleCaptions
} from "./transcription-utils";
import {
  resolveTranscriptionLanguages,
  resolveInitialTranscriptionLanguage,
  normalizeLanguageCode
} from "./transcription-language-utils";
import { resolveTranscriptionHostAdapter } from "./transcription-host-adapter";
import { resolveMetaverseRoomSessionId } from "./transcription-metaverse-host-adapter";
import {
  DEFAULT_CAPTION_OVERLAY_POSITION,
  normalizeCaptionOverlayPosition,
  parseStoredCaptionOverlayPosition,
  resolveCaptionOverlayPositionFromPreferences,
  buildCaptionOverlayPositionPreferences
} from "./transcription-caption-overlay-position-utils";
import { PROVIDERS } from "../../utils/openAI-utils";

const OPENAI_API = configs.OPENAI_API;
const MAX_CAPTIONS = 50;
const CAPTION_AUTO_DISMISS_MS = 10000;
const CAPTION_PRUNE_INTERVAL_MS = 1000;
const CAPTION_AUDIBILITY_RECHECK_VISIBLE_LINES = 3;
const LEGACY_CAPTION_OVERLAY_POSITION_STORAGE_KEY = "transcription.captionOverlayPosition.v1";
const TARGET_SAMPLE_RATE = 24000;
const TRANSCRIPTION_PROVIDER = PROVIDERS.OPENAI_TRANSCRIPTION;
const TRANSCRIPTION_ERROR_DISPLAY_MS = 15000;
const DEBUG_PREFIX = "[Transcription Debug]";

function isTranscriptionDebugEnabled() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.__TRANSCRIPTION_DEBUG__ === true;
}

function debugLog(message, data) {
  if (!isTranscriptionDebugEnabled()) return;

  if (typeof data === "undefined") {
    console.log(`${DEBUG_PREFIX} ${message}`);
    return;
  }

  console.log(`${DEBUG_PREFIX} ${message}`, data);
}

function getCurrentMicDialogEnabled() {
  return Boolean(APP.dialog?.isMicEnabled);
}

function resolveLegacyRoomId() {
  return APP.hub?.hub_id || null;
}

function resolveLegacyRoomSessionId() {
  return resolveMetaverseRoomSessionId({ app: APP });
}

function resolveLegacySpeakerIdentity() {
  const sessionId = resolveLegacyRoomSessionId();
  const presence = sessionId ? APP.hubChannel?.presence?.state?.[sessionId] : null;
  const presenceName = presence?.metas?.[0]?.profile?.displayName;
  const profileName = APP.store?.state?.profile?.displayName;

  return {
    speaker: presenceName || profileName || "Unknown",
    speakerId: APP.store?.state?.profile?.id || null,
    speakerRoomSessionId: sessionId
  };
}

function resolveLegacyCurrentDisplayName() {
  const sessionId = resolveLegacyRoomSessionId();
  const presence = sessionId ? APP.hubChannel?.presence?.state?.[sessionId] : null;
  const presenceName = presence?.metas?.[0]?.profile?.displayName;
  const profileName = APP.store?.state?.profile?.displayName;
  return presenceName || profileName || null;
}

function resolveInitialCaptionOverlayPosition() {
  const positionFromPreferences = resolveCaptionOverlayPositionFromPreferences(
    APP?.store?.state?.preferences,
    DEFAULT_CAPTION_OVERLAY_POSITION
  );

  if (typeof window === "undefined") {
    return positionFromPreferences;
  }

  try {
    const rawValue = window.localStorage.getItem(LEGACY_CAPTION_OVERLAY_POSITION_STORAGE_KEY);
    if (!rawValue) {
      return positionFromPreferences;
    }

    return parseStoredCaptionOverlayPosition(rawValue, positionFromPreferences);
  } catch (error) {
    return positionFromPreferences;
  }
}

const TranscriptionContext = createContext({
  hasTranscriptionAgent: false,
  isCCEnabled: false,
  isSendConnected: false,
  isReceiveConnected: false,
  captions: [],
  currentDisplayName: null,
  captionOverlayPosition: DEFAULT_CAPTION_OVERLAY_POSITION,
  translationEnabled: false,
  agentAllowsTranslation: false,
  targetLanguage: "en",
  availableLanguages: ["en"],
  setCaptionOverlayPosition: () => {},
  persistCaptionOverlayPosition: () => {},
  setTargetLanguage: () => {},
  toggleCC: () => {},
  toggleTranslation: () => {},
  clearCaptions: () => {},
  transcriptionError: null
});

let vadScriptsPromise = null;

const VAD_ORT_SESSION_LOG_PATCHED = "__onlandVADOrtSessionLogPatched";

function configureOrtSessionLogging(ort) {
  const inferenceSession = ort?.InferenceSession;
  if (!inferenceSession?.create || inferenceSession[VAD_ORT_SESSION_LOG_PATCHED]) {
    return;
  }

  const createSession = inferenceSession.create;
  inferenceSession.create = function createSessionWithVADLogSeverity(model, options = {}) {
    if (Array.isArray(options)) {
      return createSession.call(this, model, options);
    }

    return createSession.call(this, model, {
      ...options,
      logSeverityLevel: options?.logSeverityLevel ?? 3
    });
  };
  inferenceSession[VAD_ORT_SESSION_LOG_PATCHED] = true;
}

function configureOrtForVAD() {
  if (!window.ort) {
    return;
  }

  window.ort.env.logLevel = "error";
  configureOrtSessionLogging(window.ort);
  window.ort.env.wasm.wasmPaths = proxiedUrlFor("https://cdnjs.cloudflare.com/ajax/libs/onnxruntime-web/1.17.3/");
  window.ort.env.wasm.simd = false;
  window.ort.env.wasm.numThreads = 1;
  window.ort.env.wasm.debug = false;
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error(`Failed to load script: ${src}`)), { once: true });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

async function ensureVADScriptsLoaded() {
  if (window.vad && window.ort) {
    configureOrtForVAD();
    return;
  }

  if (!vadScriptsPromise) {
    vadScriptsPromise = (async () => {
      const ortScript = proxiedUrlFor("https://cdn.jsdelivr.net/npm/onnxruntime-web@1.17.3/dist/ort.min.js");
      const vadScript = proxiedUrlFor("https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.18/dist/bundle.min.js");

      await loadScript(ortScript);

      configureOrtForVAD();

      await loadScript(vadScript);
    })();
  }

  return vadScriptsPromise;
}

export const useTranscription = () => useContext(TranscriptionContext);

export const TranscriptionProvider = memo(function TranscriptionProvider({ children, entered, hostAdapter }) {
  const mediaDevicesManager = useRef(APP.mediaDevicesManager).current;
  const transcriptionHostAdapter = useMemo(() => resolveTranscriptionHostAdapter(hostAdapter), [hostAdapter]);

  const [hasTranscriptionAgent, setHasTranscriptionAgent] = useState(false);
  const [transcriptionAgentUuid, setTranscriptionAgentUuid] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [isCCEnabled, setIsCCEnabled] = useState(false);
  const [isSendConnected, setIsSendConnected] = useState(false);
  const [isReceiveConnected, setIsReceiveConnected] = useState(false);
  const [isReceiveSubscribed, setIsReceiveSubscribed] = useState(false);
  const [agentAllowsTranslation, setAgentAllowsTranslation] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [captionOverlayPosition, setCaptionOverlayPositionState] = useState(resolveInitialCaptionOverlayPosition);
  const [availableLanguages, setAvailableLanguages] = useState(["en"]);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState("en");
  const [transcriptionError, setTranscriptionError] = useState(null);
  const errorTimerRef = useRef(null);

  const sendSocketRef = useRef(null);
  const receiveSocketRef = useRef(null);
  const vadRef = useRef(null);
  const micStreamRef = useRef(null);
  const ownsMicStreamRef = useRef(false);
  const micContextRef = useRef(null);
  const micSourceRef = useRef(null);
  const micProcessorRef = useRef(null);
  const isMicDialogEnabledRef = useRef(Boolean(APP.dialog?.isMicEnabled));
  const sessionReadyRef = useRef(false);
  const isSpeakingRef = useRef(false);
  const audioFrameCountRef = useRef(0);
  const speechFrameEmitCountRef = useRef(0);
  const isSettingUpVADRef = useRef(false);
  const lastAudioLogAtRef = useRef(0);
  const lastAudioDropLogAtRef = useRef(0);
  const translationEnabledRef = useRef(false);
  const targetLanguageRef = useRef("en");
  const lastReceiveSubscriptionKeyRef = useRef(null);
  const captionAdmissionRef = useRef(new Map());
  const captionAdmissionSeenAtRef = useRef(new Map());
  const captionsRef = useRef([]);

  useEffect(() => {
    translationEnabledRef.current = translationEnabled;
  }, [translationEnabled]);

  useEffect(() => {
    targetLanguageRef.current = targetLanguage;
  }, [targetLanguage]);

  useEffect(() => {
    captionsRef.current = captions;
  }, [captions]);

  useEffect(() => {
    if (typeof window === "undefined" || !APP?.store?.update) {
      return;
    }

    try {
      const rawValue = window.localStorage.getItem(LEGACY_CAPTION_OVERLAY_POSITION_STORAGE_KEY);
      if (!rawValue) {
        return;
      }

      const fallbackPosition = resolveCaptionOverlayPositionFromPreferences(
        APP.store.state.preferences,
        DEFAULT_CAPTION_OVERLAY_POSITION
      );
      const legacyPosition = parseStoredCaptionOverlayPosition(rawValue, fallbackPosition);
      const persistedPosition = resolveCaptionOverlayPositionFromPreferences(
        APP.store.state.preferences,
        legacyPosition
      );

      if (legacyPosition.x !== persistedPosition.x || legacyPosition.y !== persistedPosition.y) {
        APP.store.update({
          preferences: buildCaptionOverlayPositionPreferences(legacyPosition, DEFAULT_CAPTION_OVERLAY_POSITION)
        });
      }

      window.localStorage.removeItem(LEGACY_CAPTION_OVERLAY_POSITION_STORAGE_KEY);
    } catch (error) {
      // Ignore migration issues (e.g. privacy mode).
    }
  }, []);

  useEffect(() => {
    const nextRoomId = transcriptionHostAdapter.getRoomId() || resolveLegacyRoomId();
    if (entered && nextRoomId) {
      debugLog("room detected", { roomId: nextRoomId });
      setRoomId(nextRoomId);
    }
  }, [entered, transcriptionHostAdapter]);

  useEffect(() => {
    const refreshAgent = () => {
      const transcriptionAgent = aiAgents.getAgents().find(agent => agent.provider === TRANSCRIPTION_PROVIDER);
      debugLog("agent refresh", {
        hasAgent: Boolean(transcriptionAgent),
        agentUuid: transcriptionAgent?.objectUuid || null,
        provider: transcriptionAgent?.provider || null
      });

      setHasTranscriptionAgent(Boolean(transcriptionAgent));
      setTranscriptionAgentUuid(transcriptionAgent?.objectUuid || null);

      const languages = resolveTranscriptionLanguages(transcriptionAgent);
      // Determine if the agent allows translation (default to false if totally unspecified, but we want the property value directly)
      const allowsTranslation = transcriptionAgent?.enableTranslation === true;
      setAgentAllowsTranslation(allowsTranslation);
      debugLog("resolved languages", {
        transcriptionLanguages: languages,
        browserLanguage: typeof navigator !== "undefined" ? navigator.language : null
      });
      setAvailableLanguages(languages);
      const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
      setTargetLanguage(previous => {
        const normalizedPrevious = normalizeLanguageCode(previous);
        if (normalizedPrevious && languages.includes(normalizedPrevious)) {
          return normalizedPrevious;
        }

        return resolveInitialTranscriptionLanguage(browserLanguage, languages);
      });
    };

    refreshAgent();

    window.addEventListener("ai-agent-registered", refreshAgent);
    return () => {
      window.removeEventListener("ai-agent-registered", refreshAgent);
    };
  }, []);

  const disconnectSendSocket = useCallback(() => {
    const socket = sendSocketRef.current;
    if (!socket) return;

    debugLog("disconnect send socket", { socketId: socket.id, reason: "manual_disconnect_or_reconnect" });
    socket.emit("stop_session");
    socket.disconnect();

    sendSocketRef.current = null;
    sessionReadyRef.current = false;
    setIsSendConnected(false);
  }, []);

  const connectSendSocket = useCallback(async () => {
    if (sendSocketRef.current?.connected) {
      debugLog("skip send socket connect", { reason: "already_connected", socketId: sendSocketRef.current.id });
      return;
    }

    if (!OPENAI_API || !transcriptionAgentUuid || !roomId) {
      debugLog("skip send socket connect", {
        reason: "missing_required_values",
        hasApi: Boolean(OPENAI_API),
        hasAgentUuid: Boolean(transcriptionAgentUuid),
        hasRoomId: Boolean(roomId)
      });
      return;
    }

    const token = await getToken();
    debugLog("connecting send socket", {
      endpoint: `${OPENAI_API}/transcription/send`,
      hasToken: Boolean(token)
    });
    const socket = io(`${OPENAI_API}/transcription/send`, {
      auth: token ? { token } : undefined,
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    socket.on("connect", () => {
      setIsSendConnected(true);
      const localSpeakerIdentity = transcriptionHostAdapter.getLocalSpeakerIdentity();
      const fallbackIdentity = resolveLegacySpeakerIdentity();
      const payload = buildStartTranscriptionPayload({
        uuid: transcriptionAgentUuid,
        roomId,
        speaker: localSpeakerIdentity?.speaker || fallbackIdentity.speaker,
        speakerId: localSpeakerIdentity?.speakerId || fallbackIdentity.speakerId || undefined,
        speakerRoomSessionId:
          localSpeakerIdentity?.speakerRoomSessionId || fallbackIdentity.speakerRoomSessionId || undefined
      });

      debugLog("send socket connected", { socketId: socket.id, payload });
      socket.emit("start_session", payload);
    });

    socket.on("session_started", data => {
      debugLog("send session_started", { socketId: socket.id, data });
      sessionReadyRef.current = true;
    });

    socket.on("session_ended", data => {
      debugLog("send session_ended", { socketId: socket.id, data });
      sessionReadyRef.current = false;
    });

    socket.on("disconnect", reason => {
      debugLog("send socket disconnected", { socketId: socket.id, reason });
      setIsSendConnected(false);
      sessionReadyRef.current = false;
    });

    socket.on("error", data => {
      debugLog("send socket error", data);
      console.error("[Transcription Send]", data);
      handleBlockingErrorRef.current(data);
    });

    sendSocketRef.current = socket;
  }, [transcriptionAgentUuid, roomId, transcriptionHostAdapter]);

  const disconnectReceiveSocket = useCallback(() => {
    const socket = receiveSocketRef.current;
    if (!socket) return;

    debugLog("disconnect receive socket", { socketId: socket.id, roomId });
    if (roomId) {
      socket.emit("unsubscribe", { roomId });
    }

    socket.disconnect();
    receiveSocketRef.current = null;
    setIsReceiveConnected(false);
    setIsReceiveSubscribed(false);
    lastReceiveSubscriptionKeyRef.current = null;
    captionAdmissionRef.current.clear();
    captionAdmissionSeenAtRef.current.clear();
  }, [roomId]);

  const pruneCaptionAdmissionCache = useCallback((activeCaptions = captionsRef.current) => {
    const activeKeys = new Set(activeCaptions.map(caption => buildCaptionAdmissionKey(caption)));
    const now = Date.now();

    for (const [key, decision] of captionAdmissionRef.current.entries()) {
      const lastSeenAt = captionAdmissionSeenAtRef.current.get(key) || 0;
      const shouldDeleteDeniedEntry = !decision && now - lastSeenAt > 30000;
      const shouldDeleteAllowedEntry = decision && !activeKeys.has(key);

      if (shouldDeleteDeniedEntry || shouldDeleteAllowedEntry) {
        captionAdmissionRef.current.delete(key);
        captionAdmissionSeenAtRef.current.delete(key);
      }
    }
  }, []);

  const shouldAdmitCaption = useCallback(
    (data, isTranslation = false) => {
      const captionPayload = isTranslation ? { ...data, isTranslation: true } : data;
      const key = buildCaptionAdmissionKey(captionPayload);
      const decision = Boolean(
        transcriptionHostAdapter.shouldRenderCaption({
          caption: captionPayload,
          captionKey: key,
          speakerId: data?.speakerId,
          speakerRoomSessionId: data?.speakerRoomSessionId,
          isTranslation
        })
      );

      captionAdmissionRef.current.set(key, decision);
      captionAdmissionSeenAtRef.current.set(key, Date.now());
      return decision;
    },
    [transcriptionHostAdapter]
  );

  const shouldRenderCaptionNow = useCallback(
    caption =>
      Boolean(
        transcriptionHostAdapter.shouldRenderCaption({
          caption,
          captionKey: buildCaptionAdmissionKey(caption),
          speakerId: caption?.speakerId,
          speakerRoomSessionId: caption?.speakerRoomSessionId,
          isTranslation: Boolean(caption?.isTranslation)
        })
      ),
    [transcriptionHostAdapter]
  );

  const connectReceiveSocket = useCallback(async () => {
    if (receiveSocketRef.current?.connected) {
      debugLog("skip receive socket connect", {
        reason: "already_connected",
        socketId: receiveSocketRef.current.id
      });
      return;
    }

    if (!OPENAI_API || !roomId) {
      debugLog("skip receive socket connect", {
        reason: "missing_required_values",
        hasApi: Boolean(OPENAI_API),
        hasRoomId: Boolean(roomId)
      });
      return;
    }

    const token = await getToken();
    debugLog("connecting receive socket", {
      endpoint: `${OPENAI_API}/transcription/receive`,
      hasToken: Boolean(token),
      roomId
    });
    const socket = io(`${OPENAI_API}/transcription/receive`, {
      auth: token ? { token } : undefined,
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    socket.on("connect", () => {
      debugLog("receive socket connected", { socketId: socket.id, roomId });
      setIsReceiveConnected(true);
      setIsReceiveSubscribed(false);
      const subscription = buildReceiveSubscriptionPayload({
        roomId,
        translationEnabled: translationEnabledRef.current,
        targetLanguage: targetLanguageRef.current
      });
      lastReceiveSubscriptionKeyRef.current = JSON.stringify(subscription);
      socket.emit("subscribe", subscription);
    });

    socket.on("subscribed", data => {
      debugLog("receive socket subscribed", { socketId: socket.id, data });
      setIsReceiveSubscribed(true);
    });

    socket.on("transcript_delta", data => {
      debugLog("receive transcript_delta", {
        speaker: data?.speaker,
        itemId: data?.itemId,
        contentIndex: data?.contentIndex,
        text: data?.text
      });
      if (!shouldAdmitCaption(data, false)) {
        pruneCaptionAdmissionCache();
        return;
      }
      setCaptions(previous => {
        const next = applyTranscriptDelta(previous, data, MAX_CAPTIONS);
        pruneCaptionAdmissionCache(next);
        return next;
      });
    });

    socket.on("transcript_done", data => {
      debugLog("receive transcript_done", {
        speaker: data?.speaker,
        itemId: data?.itemId,
        contentIndex: data?.contentIndex,
        text: data?.text
      });
      if (!shouldAdmitCaption(data, false)) {
        pruneCaptionAdmissionCache();
        return;
      }
      setCaptions(previous => {
        const next = applyTranscriptDone(previous, data, MAX_CAPTIONS);
        pruneCaptionAdmissionCache(next);
        return next;
      });
    });

    socket.on("translation", data => {
      debugLog("receive translation", {
        speaker: data?.speaker,
        text: data?.text,
        sourceText: data?.sourceText
      });
      if (!shouldAdmitCaption(data, true)) {
        pruneCaptionAdmissionCache();
        return;
      }
      setCaptions(previous => {
        const next = appendTranslation(previous, data, MAX_CAPTIONS);
        pruneCaptionAdmissionCache(next);
        return next;
      });
    });

    socket.on("disconnect", reason => {
      debugLog("receive socket disconnected", { socketId: socket.id, reason });
      setIsReceiveConnected(false);
      setIsReceiveSubscribed(false);
    });

    socket.on("error", data => {
      debugLog("receive socket error", data);
      console.error("[Transcription Receive]", data);
      handleBlockingErrorRef.current(data);
    });

    receiveSocketRef.current = socket;
  }, [pruneCaptionAdmissionCache, roomId, shouldAdmitCaption]);

  const cleanupVAD = useCallback(() => {
    debugLog("cleanup VAD/audio pipeline");
    try {
      if (micProcessorRef.current) {
        micProcessorRef.current.disconnect();
        micProcessorRef.current.onaudioprocess = null;
        micProcessorRef.current = null;
      }

      if (micSourceRef.current) {
        micSourceRef.current.disconnect();
        micSourceRef.current = null;
      }

      if (vadRef.current) {
        vadRef.current.pause();
        if (typeof vadRef.current.destroy === "function") {
          vadRef.current.destroy();
        }
        vadRef.current = null;
      }

      if (micStreamRef.current && ownsMicStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
      }
      micStreamRef.current = null;
      ownsMicStreamRef.current = false;

      if (micContextRef.current) {
        const context = micContextRef.current;
        micContextRef.current = null;
        if (typeof context.close === "function") {
          context.close().catch(error => {
            console.error("[Transcription] Error closing audio context", error);
          });
        }
      }
    } catch (error) {
      console.error("[Transcription] Error cleaning VAD", error);
    }

    isSpeakingRef.current = false;
    audioFrameCountRef.current = 0;
    speechFrameEmitCountRef.current = 0;
    isSettingUpVADRef.current = false;
  }, []);

  const handleBlockingError = useCallback((data) => {
    if (data?.type !== "error") return;

    const error = {
      code: data.code || "SERVICE_ERROR",
      message: data.message || "An error occurred",
      severity: data.severity || "blocking"
    };

    if (error.severity !== "blocking") return;

    setTranscriptionError(error);

    // Stop audio capture and disconnect the send session.
    // CC (receive) stays untouched — the user can still see others' captions.
    cleanupVAD();
    disconnectSendSocket();

    // Auto-clear the error bubble after 15 seconds
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    errorTimerRef.current = setTimeout(() => {
      setTranscriptionError(null);
      errorTimerRef.current = null;
    }, TRANSCRIPTION_ERROR_DISPLAY_MS);
  }, [cleanupVAD, disconnectSendSocket]);

  // Ref so socket handlers always call the latest version without needing
  // handleBlockingError in their dependency arrays (avoids unnecessary reconnects).
  const handleBlockingErrorRef = useRef(handleBlockingError);
  useEffect(() => {
    handleBlockingErrorRef.current = handleBlockingError;
  }, [handleBlockingError]);

  const emitPcm16Chunks = useCallback((pcm16, reason = "frame") => {
    const socket = sendSocketRef.current;
    if (!sessionReadyRef.current || !socket?.connected) {
      debugLog("skip audio emit", {
        reason: "session_not_ready_or_socket_disconnected",
        source: reason,
        sessionReady: sessionReadyRef.current,
        socketConnected: Boolean(socket?.connected)
      });
      return 0;
    }

    const chunks = splitInt16IntoChunks(pcm16, 2048);
    chunks.forEach(chunk => {
      socket.emit("audio_data", chunk.buffer);
    });

    debugLog("audio_data emitted chunks", {
      source: reason,
      chunkCount: chunks.length,
      totalSamples: pcm16.length,
      totalBytes: pcm16.byteLength,
      socketId: socket.id
    });

    return chunks.length;
  }, []);

  const setupVAD = useCallback(async () => {
    if (vadRef.current || micProcessorRef.current || isSettingUpVADRef.current) {
      debugLog("skip setupVAD", {
        reason: vadRef.current
          ? "vad_already_initialized"
          : micProcessorRef.current
          ? "audio_processor_already_initialized"
          : isSettingUpVADRef.current
          ? "vad_setup_in_progress"
          : "unknown"
      });
      return;
    }

    const micEnabled = getCurrentMicDialogEnabled();
    isMicDialogEnabledRef.current = micEnabled;
    if (!micEnabled) {
      debugLog("skip setupVAD", { reason: "mic_muted_in_dialog" });
      return;
    }

    if (!mediaDevicesManager?.getPreferredMicCaptureStream) {
      debugLog("skip setupVAD", {
        reason: "shared_mic_capture_unavailable"
      });
      return;
    }

    isSettingUpVADRef.current = true;

    try {
      await ensureVADScriptsLoaded();
      if (vadRef.current) {
        debugLog("skip setupVAD", { reason: "vad_already_initialized_after_async_load" });
        return;
      }

      if (!window.vad?.MicVAD) {
        debugLog("skip setupVAD", { reason: "vad_library_not_available" });
        return;
      }

      const stream = mediaDevicesManager.getPreferredMicCaptureStream();
      if (!stream) {
        debugLog("skip setupVAD", { reason: "selected_mic_capture_unavailable" });
        return;
      }

      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextCtor) {
        debugLog("skip setupVAD", { reason: "audio_context_unavailable" });
        return;
      }

      const context = new AudioContextCtor();
      if (context.state === "suspended") {
        await context.resume();
      }

      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(4096, 1, 1);

      debugLog("creating VAD/audio pipeline", {
        sampleRate: context.sampleRate,
        targetRate: TARGET_SAMPLE_RATE,
        sessionReady: sessionReadyRef.current
      });

      const vad = await window.vad.MicVAD.new({
        stream,
        onSpeechStart: () => {
          debugLog("VAD speech start");
          isSpeakingRef.current = true;
          speechFrameEmitCountRef.current = 0;
        },
        onSpeechEnd: () => {
          debugLog("VAD speech end");
          isSpeakingRef.current = false;
        }
      });

      processor.onaudioprocess = event => {
        try {
          const socket = sendSocketRef.current;
          const liveMicEnabled = getCurrentMicDialogEnabled();
          isMicDialogEnabledRef.current = liveMicEnabled;
          if (!isSpeakingRef.current || !sessionReadyRef.current || !socket?.connected || !liveMicEnabled) {
            const now = Date.now();
            if (now - lastAudioDropLogAtRef.current > 2000) {
              debugLog("audio processor skipped", {
                isSpeaking: isSpeakingRef.current,
                sessionReady: sessionReadyRef.current,
                socketConnected: Boolean(socket?.connected),
                micEnabled: liveMicEnabled
              });
              lastAudioDropLogAtRef.current = now;
            }
            return;
          }

          const input = event.inputBuffer.getChannelData(0);
          const downsampled = downsampleFloat32(input, context.sampleRate, TARGET_SAMPLE_RATE);
          if (!downsampled.length) {
            return;
          }

          const pcm16 = float32ToInt16(downsampled);
          const chunksEmitted = emitPcm16Chunks(pcm16, "processor");
          audioFrameCountRef.current += 1;
          speechFrameEmitCountRef.current += chunksEmitted;

          const now = Date.now();
          if (now - lastAudioLogAtRef.current > 1000) {
            debugLog("audio_data emitted", {
              processorFramesLastSecond: audioFrameCountRef.current,
              chunksLastSecond: speechFrameEmitCountRef.current,
              bytesPerFrame: pcm16.byteLength,
              inputRate: context.sampleRate,
              outputRate: TARGET_SAMPLE_RATE,
              socketId: socket.id
            });
            audioFrameCountRef.current = 0;
            speechFrameEmitCountRef.current = 0;
            lastAudioLogAtRef.current = now;
          }
        } catch (error) {
          console.error("[Transcription] Error processing audio frame", error);
        }
      };

      source.connect(processor);
      processor.connect(context.destination);

      vad.start();
      vadRef.current = vad;
      micStreamRef.current = stream;
      ownsMicStreamRef.current = false;
      micContextRef.current = context;
      micSourceRef.current = source;
      micProcessorRef.current = processor;
      debugLog("VAD+audio processor started", {
        inputRate: context.sampleRate,
        outputRate: TARGET_SAMPLE_RATE
      });
    } catch (error) {
      console.error("[Transcription] Error setting up VAD", error);
      cleanupVAD();
    } finally {
      isSettingUpVADRef.current = false;
    }
  }, [emitPcm16Chunks, cleanupVAD, mediaDevicesManager]);

  useEffect(() => {
    const shouldConnect = shouldConnectSendTranscriptionSession({
      entered,
      hasTranscriptionAgent,
      roomId
    });

    if (shouldConnect) {
      debugLog("auto connect send socket", {
        entered,
        hasTranscriptionAgent,
        roomId,
        isCCEnabled,
        isReceiveSubscribed,
        shouldConnect
      });
      connectSendSocket();
    } else {
      debugLog("skip auto connect send socket", {
        entered,
        hasTranscriptionAgent,
        roomId,
        isCCEnabled,
        isReceiveSubscribed,
        shouldConnect
      });
      disconnectSendSocket();
    }

    return () => {
      disconnectSendSocket();
    };
  }, [entered, hasTranscriptionAgent, roomId, isCCEnabled, isReceiveSubscribed, connectSendSocket, disconnectSendSocket]);

  useEffect(() => {
    if (!isSendConnected) {
      debugLog("send disconnected, cleaning VAD");
      cleanupVAD();
      return;
    }

    const initialMicEnabled = getCurrentMicDialogEnabled();
    isMicDialogEnabledRef.current = initialMicEnabled;
    debugLog("mic state snapshot on send connect", { enabled: initialMicEnabled });
    if (!initialMicEnabled) {
      debugLog("send connected but mic muted; waiting for unmute to start transcription capture");
      cleanupVAD();
    } else {
      setupVAD();
    }

    const eventTarget = mediaDevicesManager?._scene || window.APP;
    if (!eventTarget?.addEventListener) {
      return undefined;
    }

    const onMicMutedStateChanged = ({ enabled }) => {
      const isEnabled = Boolean(enabled);
      isMicDialogEnabledRef.current = isEnabled;
      debugLog("mic-state-changed", { enabled: isEnabled });

      if (!isEnabled) {
        debugLog("mic muted, stopping transcription capture");
        cleanupVAD();
        return;
      }

      if (isSendConnected) {
        debugLog("mic unmuted, restarting transcription capture");
        setTimeout(() => {
          setupVAD();
        }, 50);
      }
    };

    APP.dialog?.on("mic-state-changed", onMicMutedStateChanged);

    const onMicStart = () => {
      debugLog("mic share started event");
      cleanupVAD();
      if (isMicDialogEnabledRef.current) {
        setTimeout(() => {
          setupVAD();
        }, 50);
      }
    };

    const onMicEnd = () => {
      debugLog("mic share ended event");
      cleanupVAD();
    };

    eventTarget.addEventListener(MediaDevicesEvents.MIC_SHARE_STARTED, onMicStart);
    eventTarget.addEventListener(MediaDevicesEvents.MIC_SHARE_ENDED, onMicEnd);

    return () => {
      APP.dialog?.off("mic-state-changed", onMicMutedStateChanged);
      eventTarget.removeEventListener(MediaDevicesEvents.MIC_SHARE_STARTED, onMicStart);
      eventTarget.removeEventListener(MediaDevicesEvents.MIC_SHARE_ENDED, onMicEnd);
      cleanupVAD();
    };
  }, [isSendConnected, setupVAD, cleanupVAD, mediaDevicesManager]);

  useEffect(() => {
    if (isCCEnabled) {
      debugLog("CC enabled, connect receive");
      connectReceiveSocket();
    } else {
      debugLog("CC disabled, disconnect receive");
      disconnectReceiveSocket();
    }

    return () => {
      disconnectReceiveSocket();
    };
  }, [isCCEnabled, connectReceiveSocket, disconnectReceiveSocket]);

  useEffect(() => {
    const socket = receiveSocketRef.current;
    if (!socket?.connected || !isReceiveSubscribed || !roomId) {
      return;
    }

    const subscription = buildReceiveSubscriptionPayload({
      roomId,
      translationEnabled,
      targetLanguage
    });
    const subscriptionKey = JSON.stringify(subscription);
    if (lastReceiveSubscriptionKeyRef.current === subscriptionKey) {
      return;
    }

    lastReceiveSubscriptionKeyRef.current = subscriptionKey;
    debugLog("receive preferences updated", { socketId: socket.id, subscription });
    socket.emit("update_preferences", subscription);
  }, [isReceiveSubscribed, roomId, translationEnabled, targetLanguage]);

  useEffect(() => {
    return () => {
      cleanupVAD();
      disconnectSendSocket();
      disconnectReceiveSocket();
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, [cleanupVAD, disconnectSendSocket, disconnectReceiveSocket]);

  const toggleCC = useCallback(() => {
    debugLog("toggle CC clicked");
    setIsCCEnabled(previous => !previous);
  }, []);

  const clearCaptions = useCallback(() => {
    captionAdmissionRef.current.clear();
    captionAdmissionSeenAtRef.current.clear();
    setCaptions([]);
  }, []);

  const toggleTranslation = useCallback(() => {
    debugLog("toggle translation clicked");
    setTranslationEnabled(previous => !previous);
  }, []);

  const setCaptionOverlayPosition = useCallback(nextPosition => {
    setCaptionOverlayPositionState(previous => {
      const resolvedPosition = typeof nextPosition === "function" ? nextPosition(previous) : nextPosition;
      const normalized = normalizeCaptionOverlayPosition(resolvedPosition, previous);

      if (normalized.x === previous.x && normalized.y === previous.y) {
        return previous;
      }

      return normalized;
    });
  }, []);

  const persistCaptionOverlayPosition = useCallback(
    nextPosition => {
      const resolvedPosition = typeof nextPosition === "function" ? nextPosition(captionOverlayPosition) : nextPosition;
      const normalized = normalizeCaptionOverlayPosition(resolvedPosition, captionOverlayPosition);

      if (normalized.x !== captionOverlayPosition.x || normalized.y !== captionOverlayPosition.y) {
        setCaptionOverlayPositionState(normalized);
      }

      const persistedPosition = resolveCaptionOverlayPositionFromPreferences(
        APP?.store?.state?.preferences,
        normalized
      );
      if (persistedPosition.x === normalized.x && persistedPosition.y === normalized.y) {
        return;
      }

      APP?.store?.update?.({
        preferences: buildCaptionOverlayPositionPreferences(normalized, DEFAULT_CAPTION_OVERLAY_POSITION)
      });
    },
    [captionOverlayPosition]
  );

  useEffect(() => {
    debugLog("translation settings updated", { translationEnabled, targetLanguage });
  }, [translationEnabled, targetLanguage]);

  useEffect(() => {
    if (!isCCEnabled) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      const now = Date.now();
      setCaptions(previous => {
        const afterTimePrune = pruneExpiredCaptions(previous, CAPTION_AUTO_DISMISS_MS, now);
        const next = pruneDynamicallyInaudibleCaptions(afterTimePrune, shouldRenderCaptionNow, {
          visibleLineCount: CAPTION_AUDIBILITY_RECHECK_VISIBLE_LINES
        });
        if (next === previous) {
          return previous;
        }

        pruneCaptionAdmissionCache(next);
        return next;
      });
    }, CAPTION_PRUNE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isCCEnabled, pruneCaptionAdmissionCache, shouldRenderCaptionNow]);

  const currentDisplayName = transcriptionHostAdapter.getCurrentDisplayName() || resolveLegacyCurrentDisplayName();

  return (
    <TranscriptionContext.Provider
      value={{
        hasTranscriptionAgent,
        isCCEnabled,
        isSendConnected,
        isReceiveConnected,
        captions,
        currentDisplayName,
        captionOverlayPosition,
        translationEnabled,
        targetLanguage,
        availableLanguages,
        agentAllowsTranslation,
        setCaptionOverlayPosition,
        persistCaptionOverlayPosition,
        setTargetLanguage,
        toggleCC,
        toggleTranslation,
        clearCaptions,
        transcriptionError
      }}
    >
      {children}
    </TranscriptionContext.Provider>
  );
});

TranscriptionProvider.propTypes = {
  children: PropTypes.node.isRequired,
  entered: PropTypes.bool.isRequired,
  hostAdapter: PropTypes.shape({
    getRoomId: PropTypes.func,
    getLocalSpeakerIdentity: PropTypes.func,
    getCurrentDisplayName: PropTypes.func,
    shouldRenderCaption: PropTypes.func
  })
};
